import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CommonItem, GameState, Investigator, UserAction } from '../api';
import { fightControlKind, fightEquipment } from './fightPresentation';
import { GameDialog } from './GameDialog';

/** Shared by the inline Fight panel and its mobile footer. Server actions determine equipment eligibility. */
export function useFightActionExecution({ state, investigator, actions, loading, viewerName, contentRef, message, onExecuteAction }: {
  state: GameState; investigator?: Investigator; actions: UserAction[]; loading: boolean; viewerName: string;
  contentRef: React.RefObject<HTMLDivElement | null>; message?: string; onExecuteAction: (id: string) => void;
}) {
  const scope = JSON.stringify([state.gameId, viewerName, investigator?.name, state.currentFightingMonster]);
  const [pending, setPending] = useState<{ scope: string; action: string } | null>(null);
  const consumed = useRef(false);
  const decision = JSON.stringify([actions, state.skillCheckInformation, state.encounterInformation, state.damageInformation, message]);
  const awaitingDecision = useRef<string | null>(null);
  const scrollToDecision = () => {
    const body = document.querySelector<HTMLElement>('.game-fullscreen-actions-body');
    if (body) body.scrollTop = 0;
    else (contentRef.current?.querySelector<HTMLElement>('.sticky-actions-wrapper')
      ?? contentRef.current?.querySelector<HTMLElement>('#game-actions'))?.scrollIntoView?.({ block: 'start' });
  };
  useLayoutEffect(() => {
    if (awaitingDecision.current !== null && !loading && awaitingDecision.current !== decision) {
      scrollToDecision();
      awaitingDecision.current = null;
    }
  });
  useEffect(() => { awaitingDecision.current = null; setPending(null); }, [state.gameId, viewerName, investigator?.name]);
  const pendingAction = pending?.scope === scope
    ? actions.find(action => JSON.stringify(action) === pending.action && !action.disabled) : undefined;
  useEffect(() => { if (pending && !pendingAction) setPending(null); }, [pending, pendingAction]);
  const dispatch = (action: UserAction) => {
    const kind = fightControlKind(action, state.currentFightingMonster);
    if (kind === 'fight' || kind === 'evade') {
      scrollToDecision();
      awaitingDecision.current = decision;
    }
    onExecuteAction(action.id);
  };
  const execute = (id: string) => {
    const action = actions.find(candidate => candidate.id === id);
    if (loading || !action || action.disabled) return;
    const needsEquipment = investigator && fightEquipment(investigator, actions).some(({ entry, action: equip }) =>
      !(entry.item as CommonItem).equipped && ((entry.item as CommonItem).hands ?? 0) > 0
      && equip?.id.startsWith('equip_') && !equip.disabled);
    if (fightControlKind(action, state.currentFightingMonster) === 'fight' && needsEquipment) {
      consumed.current = false;
      setPending({ scope, action: JSON.stringify(action) });
    } else dispatch(action);
  };
  const confirmation = pendingAction && <GameDialog title="Fight without equipping weapons?" onClose={() => setPending(null)}>
    <p>You have free hands and equipment available. Do you want to fight without equipping weapons?</p>
    <button type="button" onClick={() => setPending(null)}>Equip weapons</button>
    <button type="button" disabled={loading} onClick={() => {
      if (loading || consumed.current) return;
      consumed.current = true;
      setPending(null);
      dispatch(pendingAction);
    }}>Fight anyway</button>
  </GameDialog>;
  return { execute, confirmation };
}
